/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import PageSkeleton from '../../components/common/page-skeleton';
import { Button } from 'sharedUi/Button';
import { Toast } from 'sharedUi/Toast';
import { Message } from 'sharedUi/Message';
import { Tag } from 'sharedUi/Tag';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { valueSetsService } from '../../services/value-sets.service';
import type { ValueSetLocalized } from '../../types';
import { extractErrorMessage } from '../../utils/error-handler';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import { AuditInfoCard } from '../../components/common/audit-info-card';
import { Field, Section } from '../../components/common/detail-view';
import ScreenHeader from '../../components/common/screen-header';
import { useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';

const ValueSetView = () => {
  const navigate = useAppNavigate();
  const { key } = useParams<{ key: string }>();
  const toast = useRef<Toast>(null);

  const canUpdate = useCanUpdate(JOB_NAMES.VALUE_SETS);
  const canDelete = useCanDelete(JOB_NAMES.VALUE_SETS);

  const [valueSet, setValueSet] = useState<ValueSetLocalized | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);


  // Set breadcrumb title to show value set key instead of UUID
  useBreadcrumbTitle(valueSet?.key);

  // Stable fetch function (prevents infinite loops)
  const fetchValueSet = useCallback(async () => {
    if (!key) return;

    try {
      setLoading(true);
      const data = await valueSetsService.getByKey(key);
      setValueSet(data);
    } catch (err: any) {
      setError(
        extractErrorMessage(err, 'Failed to fetch value set details')
      );
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    if (!key) {
      navigate('/value-sets');
      return;
    }
    fetchValueSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const handleEdit = () => {
    if (valueSet) navigate(`/value-sets/${valueSet.key}/edit`);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!valueSet) return;

    try {
      setDeleting(true);
      await valueSetsService.delete(valueSet.key);

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Value Set deleted successfully',
        life: 3000,
      });

      setTimeout(() => {
        navigate('/value-sets');
      }, 1500);
    } catch (err: any) {
      setError(
        extractErrorMessage(err, 'Failed to delete value set')
      );
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: extractErrorMessage(err, 'Failed to delete value set'),
        life: 5000,
      });
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };


  if (loading) {
    return (
      <PageSkeleton />
    );
  }

  if (error || !valueSet) {
    return (
      <div className="p-4">
        <Message severity="error" text={error || 'Value set not found'} className="mb-4 w-full" />
        <Button
          label="Back to Value Sets"
          icon="pi pi-arrow-left"
          severity="secondary"
          outlined
          onClick={() => navigate('/value-sets')}
        />
      </div>
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <div className="p-3" data-testid="SCR-ValueSet-View">
        {/* Header Section */}
        <ScreenHeader icon="pi pi-cog" title="Value Set Details" onBack={() => navigate('/value-sets')} />

        {/* Value Set Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
          <div className="flex items-start gap-2.5">
            {/* Icon */}
            <div className="w-12 h-12 rounded bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
              <i className="pi pi-cog text-purple-600 dark:text-purple-400" style={{ fontSize: '1.25rem' }} />
            </div>

            <div>
              <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                <h2 className="text-base font-semibold text-ink">{valueSet.key}</h2>
                {valueSet.status && (
                  <Tag value={valueSet.status} severity={valueSet.status === 'active' ? 'success' : 'warning'} className="text-xs px-1.5 py-0.5" />
                )}
                <Tag
                  value={`${valueSet.items.length} Item${valueSet.items.length !== 1 ? 's' : ''}`}
                  severity="info"
                  className="text-xs px-1.5 py-0.5"
                />
              </div>
              <p className="text-xs text-ink-muted">
                {valueSet.description || 'No description'}
              </p>
            </div>
          </div>

          {/* Edit/Delete buttons */}
          {!((valueSet as any).isDelete || valueSet.status === 'archived') && (
            <div className="flex gap-1.5 sm:self-center">
              {canUpdate && (
                <Button
                  label="Edit"
                  icon="pi pi-pencil"
                  onClick={handleEdit}
                  size="small"
                />
              )}
              {canDelete && (
                <Button
                  label="Delete"
                  icon="pi pi-trash"
                  severity="danger"
                  onClick={handleDeleteClick}
                  size="small"
                />
              )}
            </div>
          )}
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 gap-2">
          {/* Details Section */}
          <Section title="Details">
            <Field label="Key" value={valueSet.key} mono />
            <Field label="Module" value={valueSet.module} />
            <Field label="Status" value={valueSet.status} />
            <Field label="Items" value={valueSet.items.length} />
            <Field label="Description" value={valueSet.description} />
          </Section>

          {/* Enumeration Items Section */}
          <div className="ds-card p-3">
            <h2 className="text-sm font-semibold text-ink mb-2">
              Enumeration Items ({valueSet.items.length})
            </h2>

            {valueSet.items.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line">
                      <th className="text-left py-2 px-2 text-ink-muted font-medium" style={{ width: '60px' }}>#</th>
                      <th className="text-left py-2 px-2 text-ink-muted font-medium">Code</th>
                      <th className="text-left py-2 px-2 text-ink-muted font-medium">Label</th>
                    </tr>
                  </thead>
                  <tbody>
                    {valueSet.items.map((item: any, index: number) => (
                      <tr key={index} className="border-b border-line last:border-b-0">
                        <td className="py-2 px-2 text-ink-muted">{index + 1}</td>
                        <td className="py-2 px-2 text-ink font-medium">{item.code}</td>
                        <td className="py-2 px-2 text-ink">{item.label}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-ink-muted">
                <i className="pi pi-inbox text-4xl mb-2" />
                <p className="text-sm font-medium">No items found</p>
              </div>
            )}
          </div>

          <AuditInfoCard
            createdAt={(valueSet as any).createdAt}
            updatedAt={(valueSet as any).updatedAt}
            createdBy={(valueSet as any).createdBy}
            updatedBy={(valueSet as any).updatedBy}
          />
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        visible={deleteDialogOpen}
        onCancel={handleDeleteCancel}
        onDelete={handleDeleteConfirm}
        message="Are you sure you want to delete this value set? This action cannot be undone and may affect system functionality."
        header="Confirm Delete"
        loading={deleting}
        testId="ValueSetView.Dialog.Delete"
      />
    </>
  );
};

export default ValueSetView;
